import { useEffect, useRef, useState } from 'react';

/**
 * Panelin hareket yardımcıları.
 *
 * İlke: hareket, değişimi anlatmak içindir. Sayı sayacı "bu değer yenilendi"
 * der; satırların sırayla belirmesi okuma sırasını gösterir. Süsleme yok.
 *
 * Hepsi `prefers-reduced-motion` tercihine saygı duyar — o durumda değer
 * anında son hâline oturur.
 */

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/**
 * Sayıyı hedefine doğru yumuşakça çıkarır.
 *
 * Gösterge kartlarında kullanılıyor: panel yenilendiğinde hangi sayının
 * değiştiği, rakamın hareketinden anlaşılıyor.
 */
export function useCountUp(target: number, durationMs = 650): number {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    const from = fromRef.current;
    if (from === target) return;

    if (prefersReducedMotion()) {
      fromRef.current = target;
      setValue(target);
      return;
    }

    const start = performance.now();
    const delta = target - from;

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // Yumuşak çıkış: hızlı başlar, sonuna doğru yavaşlar.
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(from + delta * eased));
      if (t < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target, durationMs]);

  return value;
}

/**
 * Bir öğenin sıra numarasına göre gecikme stili.
 * Uzun listelerde gecikme birikmesin diye sekizinci öğeden sonra sabitlenir.
 */
export function staggerDelay(index: number, stepMs = 40): React.CSSProperties {
  if (prefersReducedMotion()) return {};
  return { animationDelay: `${Math.min(index, 8) * stepMs}ms` };
}

/**
 * Bir SVG yolunun uzunluğunu ölçer; çizgi grafiğini soldan sağa çizmek için.
 * Ölçüm tarayıcıda yapıldığından yol değiştiğinde yeniden hesaplanır.
 */
export function usePathLength(d: string): {
  ref: React.RefObject<SVGPathElement | null>;
  length: number;
} {
  const ref = useRef<SVGPathElement | null>(null);
  const [length, setLength] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    try {
      setLength(node.getTotalLength());
    } catch {
      // jsdom gibi ortamlarda ölçüm yapılamaz; animasyon sessizce atlanır.
      setLength(0);
    }
  }, [d]);

  return { ref, length };
}

/**
 * Öğe görünür alana girdiğinde `true` döner. Uzun sayfalarda animasyonu
 * yalnızca görünen kısımda çalıştırmak için.
 */
export function useInView<T extends HTMLElement>(options?: IntersectionObserverInit) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          setInView(true);
          io.unobserve(entry.target);
        }
      },
      options ?? { rootMargin: '0px 0px -10% 0px', threshold: 0.1 },
    );

    io.observe(node);
    return () => io.disconnect();
  }, [options]);

  return { ref, inView };
}
